import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  Stack,
  Select,
  MenuItem,
  Pagination,
  TableRow,
  Snackbar,
  Alert,
  Paper,
  useTheme,
  Checkbox,
} from "@mui/material";
import {
  getAllLocationGroupsListAction,
  saveLocationGroupsAction,
  updateLocationGroupsAction,
} from "../../redux/actions/locationGroupActions";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getAllLocationAction } from "../../redux/actions/locationActions";
import { getUser as getUserAction } from "../../redux/actions/userActions";

const LocationList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const user = useSelector((state) => state.user.user);
  const [customerId, setCustomerId] = useState(null);

  const [selectedLocationIds, setSelectedLocationIds] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [isEdit, setIsEdit] = useState(false);
  const [groupId, setGroupId] = useState(null);
  const [error, setError] = useState("");

  const locationDetails = useSelector(
    (state) => state.location.allLocation || []
  );

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    dispatch(getUserAction());
  }, [dispatch]);

  useEffect(() => {
    if (user) {
      setCustomerId(user.id || null);
    }
  }, [user]);

  useEffect(() => {
    if (location.state) {
      setGroupName(location.state.groupName || "");

      const locationIds = location.state.selectedLocations.map(
        (loc) => loc.id || loc.locationId
      );
      setSelectedLocationIds(locationIds);

      setIsEdit(location.state.isEdit);
      setGroupId(location.state.groupId || null);
    }
    dispatch(getAllLocationAction());
  }, [location.state]);

  const handleCheckboxChange = (id) => {
    setSelectedLocationIds((prev) =>
      prev.includes(id)
        ? prev.filter((locationId) => locationId !== id)
        : [...prev, id]
    );
  };

const handleCreateOrUpdateGroupClick = () => {
  if (!groupName) {
    setError("Please provide a group name.");
    return;
  }

  if (selectedLocationIds.length === 0) {
    setError("Please select at least one location.");
    return;
  }

  setError("");

  const groupData = {
    name: groupName,
    locationIds: selectedLocationIds,
    status: "Active",
  };

  const updatedGroupLocation = {
    id: groupId,
    name: groupName,
    locationIds: selectedLocationIds,
    status: "Active",
  };

  const finalFormValues = { ...groupData, customerId };
  const UpdatedFinalFormValues = { ...updatedGroupLocation, customerId };

  const submitData = isEdit && groupId
    ? updateLocationGroupsAction(UpdatedFinalFormValues)
    : saveLocationGroupsAction(finalFormValues);

  dispatch(submitData)
    .then(() => {
      return dispatch(getAllLocationGroupsListAction(0, 100));
    })
    .then(() => {
      setSnackbar({
        open: true,
        message: isEdit
          ? "Location Group Updated successfully"
          : "Location Group Created successfully",
        severity: "success",
      });

      setTimeout(() => {
        navigate("/locationgrp");
      }, 2000);
    })
    .catch((error) => {
      console.error("❌ Save/Update failed:", error);
      setSnackbar({
        open: true,
        message: "Something went wrong. Please try again.",
        severity: "error",
      });
    });

  setSelectedLocationIds([]);
  setGroupName("");
  setIsEdit(false);
  setGroupId(null);
  setError("");
};

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const handleCancel = () => {
    setGroupName("");
    setError("");
    navigate("/locationgrp");
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedRows = locationDetails.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box
      sx={{
        padding: "20px",
        margin: "20px",
        border: "1px solid  rgba(57, 57, 57, 0.1)",
        boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
      }}
    >
      <Typography
        variant="h5"
        sx={{ marginBottom: 3, color: theme.palette.primary.main }}
      >
        All Location
      </Typography>
      <Box sx={{ marginBottom: "20px" }}>
        <Typography
          variant="body1"
          sx={{
            fontFamily: "Poppins",
            fontSize: "14px",
            fontWeight: "500",
            lineHeight: "19.6px",
            textAlign: "left",
            marginBottom: "6px",
          }}
        >
          Location Group Name
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <TextField
            variant="outlined"
            size="small"
            value={groupName}
            onChange={(e) => {
              const value = e.target.value;
              if (
                value === '' ||
                /^(?!.* {2,})[A-Za-z0-9&\-_ ]*$/.test(value)
              ) {
                setGroupName(value);
                setError('');
              } else {
                setError('only alphanumeric, single space, &, hyphen,Underscore allowed');
              }
            }}
            error={!!error}
            helperText={error}
            sx={{
              width: "230px",
              height: "55px",
              "& .MuiInputBase-root": {
                height: "55px",
              },
            }}
          />
          <Box sx={{ display: "flex", marginTop: "2px", marginLeft: "10px" }}>
            <Button
              variant="outlined"
              size="large"
              onClick={handleCreateOrUpdateGroupClick}
              sx={{ marginTop: "-5px", }}
            >
              {isEdit ? "Update Group" : "Create Group"}
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={handleCancel}
              sx={{ marginTop: "-5px", marginLeft: "10px", }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Box>
      <TableContainer component={Paper} sx={{ marginTop: "40px" }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                className="styled-table-heading"
                padding="checkbox"
              ></TableCell>
              <TableCell className="styled-table-heading">
                Description
              </TableCell>
              <TableCell className="styled-table-heading">Address</TableCell>
              <TableCell className="styled-table-heading">City</TableCell>
              <TableCell className="styled-table-heading">State</TableCell>
              <TableCell className="styled-table-heading">Zip Code</TableCell>
              <TableCell className="styled-table-heading">Phone</TableCell>
              <TableCell className="styled-table-heading">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.map((location, index) => (
              <TableRow key={location.id} className="styled-table-row">
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedLocationIds.includes(location.id)}
                    onChange={() => handleCheckboxChange(location.id)}
                    id={`active-${index}`}
                  />
                </TableCell>
                <TableCell>{location.locationDescription}</TableCell>
                <TableCell>{`${location.address1} ${location.address2}`}</TableCell>
                <TableCell>{location.city}</TableCell>
                <TableCell>{location.state}</TableCell>
                <TableCell>{location.zipCode}</TableCell>
                <TableCell>{location.phone}</TableCell>
                <TableCell>{location.isActive ? "Yes" : "No"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {locationDetails.length > 0 && (
        <Box
          sx={{
            width: '100%',
            overflow: 'hidden',
            padding: '16px 0',
          }}
        >
          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
            sx={{
              '@media (max-width: 600px)': {
                flexDirection: 'column',
                alignItems: 'center',
              },
            }}
          >
            <Box display="flex" alignItems="center">
              <Typography variant="body2" sx={{ fontSize: '12px', marginRight: '8px' }}>
                Rows per page:
              </Typography>
              <Select
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                size="small"
                sx={{ fontSize: '12px', minWidth: '80px' }}
              >
                {[5, 10, 25].map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Pagination
              count={Math.ceil(locationDetails.length / rowsPerPage)}
              page={page + 1}
              onChange={(event, value) => handleChangePage(event, value - 1)}
              showFirstButton
              showLastButton
              siblingCount={1}
              boundaryCount={1}
              size="small"
            />
          </Stack>
        </Box>
      )}

      </TableContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LocationList;
